// Light and dark, stored, the same switch admin.blckpk.com already has.
//
// NOT A CLIENT THEME. The console stays Nova's in both modes — XP1's neutral on
// true black, or the same neutral on near-white paper. A surface that repainted
// itself in whoever's activation is open would answer "am I looking at the right
// client's data" with a colour that changes as you navigate. This does not do
// that.
//
// Runs at import time so the first paint already matches the stored choice.
// There is no inline script in index.html: script-src is 'self' and an inline
// flash-prevention snippet would be the first 'unsafe-inline' on a surface
// that exists to keep scripts out.

import { useEffect, useState, type ReactNode } from "react";
import { ModeIcon } from "@nova/desk-ui/parts";

const KEY = "xpConsoleTheme";

export type ConsoleTheme = "light" | "dark";

export function readTheme(): ConsoleTheme {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch {
    /* private mode */
  }
  try {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      if (window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
    }
  } catch {
    /* jsdom, or a browser that will not answer */
  }
  return "light";
}

/**
 * THREE ATTRIBUTES FOR ONE CHOICE, WHICH IS TWO VOCABULARIES AND NOT A MISTAKE.
 *
 * data-theme is this package's own and is what tokens/desk.css keys its dark
 * --xp-* block off. data-system and data-mode are what packages/desk-ui keys its
 * --d-* blocks off, and they are set here rather than on a frame element in the
 * shell so that every screen is themed — including the ones that are not the
 * desk shell. A sign-in has no rail and no stage and still has to be painted.
 *
 * They are written together, in one function, on one element, because the
 * failure they invite is a document in dark --xp-* and light --d-*: half the
 * page inverted, no error anywhere, and a bug report that reads "the console
 * looks broken sometimes". Setting them apart in two modules is how that
 * happens. check.mjs asserts all three move together.
 *
 * data-system is constant. It is written on every call anyway rather than once
 * at import, so that a caller who reaches for applyTheme to repaint a detached
 * document — which the checks do — gets a complete set rather than a mode with
 * no system, which matches no block at all and resolves every --d- name to
 * nothing.
 */
const hearTheme = new Set<(theme: ConsoleTheme) => void>();

export function applyTheme(theme: ConsoleTheme): void {
  const next = theme === "light" ? "light" : "dark";
  const root = document.documentElement;
  root.setAttribute("data-theme", next);
  // Charcoal: XP1 light, and XP1's dark lifted off black (packages/desk-ui
  // tokens.css). Since the October hand-off XP1 dark is true black and the
  // lifted set the console has always worn is its own system.
  root.setAttribute("data-system", "charcoal");
  root.setAttribute("data-mode", next);
  root.style.colorScheme = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* ignore */
  }
  // The chrome and the account page each draw this switch. Telling them here
  // is what keeps the pressed button the same on both after either one moves.
  for (const hear of hearTheme) hear(next);
}

if (typeof document !== "undefined") {
  applyTheme(readTheme());
}

const MODES: ReadonlyArray<readonly [ConsoleTheme, string]> = [
  ["light", "Light"],
  ["dark", "Dark"],
];

/**
 * The switch itself: the desk's .modeset, drawn with the desk's marks.
 *
 * IT USED TO BE A PILL WITH A BLACK LOZENGE SLIDING UNDER IT, and both halves
 * of that were this package's invention. The lozenge was --xp-accent, which on
 * the light token set is solid black, so the chosen mode read as a filled
 * button in a way nothing else on either desk does. The design pack's answer is
 * quieter and is already in packages/desk-ui as .modeset: --d-nav-on behind the
 * pressed one and --d-nav-mark as its edge, which is the same treatment the
 * rail gives the page you are on. Two controls meaning "this is the one you are
 * on" ought to look like each other, and a black fill in a menu of grey text
 * reads as a thing that is about to do something rather than a thing that is
 * already true.
 *
 * The two icons were this package's own as well, a filled circle with spokes
 * and a filled crescent, and they are the shared stroked pair now. That is not
 * only tidiness: at 17px a filled crescent against --d-nav-on is a dark blob,
 * where the stroked one keeps its shape. ModeIcon's kinds are named for the two
 * modes, so the mapping is the identity and there is nothing to keep in step.
 *
 * A GROUP OF TWO PRESSED BUTTONS RATHER THAN ONE TOGGLE, which is deliberate
 * and matches the pack. A single button that flips has to say what it will do,
 * and every wording for that is ambiguous about whether the sun means "you are
 * in light" or "press for light". Two buttons, one of them aria-pressed, say
 * which you are in and what the other one is at the same time.
 */
export function ThemeToggle(): ReactNode {
  const [theme, setTheme] = useState<ConsoleTheme>(() =>
    typeof document === "undefined"
      ? "light"
      : ((document.documentElement.getAttribute("data-theme") as ConsoleTheme | null) ?? readTheme()),
  );

  const set = (next: ConsoleTheme): void => {
    applyTheme(next);
    setTheme(next);
  };

  // The account page draws a second copy of this switch. Both have to agree
  // about which mode is pressed, so each one hears the choice the other writes.
  useEffect(() => {
    hearTheme.add(setTheme);
    return () => {
      hearTheme.delete(setTheme);
    };
  }, []);

  return (
    <div className="modeset" role="group" aria-label="Appearance">
      {MODES.map(([mode, name]) => (
        <button
          key={mode}
          type="button"
          title={name}
          aria-label={name}
          aria-pressed={theme === mode}
          onClick={() => set(mode)}
        >
          <ModeIcon kind={mode} />
        </button>
      ))}
    </div>
  );
}
